import { Component, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import type { ModalOptions } from '@ionic/angular';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
// import { Router } from '@angular/router';
// import { Location } from '@angular/common';

import {
    AddressVerificationService, AuthService, IsoCountriesService,
    IsoTimezonesService, UsersService
} from '@shared/services';
import { RadioModalPage, ViewBasePage } from '@shared/pages';
import { Constants } from '@shared/models';
import type {
    TAccountCreationQuestionsAndAnswers, TAddress, TIsoCountry, TCreateAccountInfo, TRegion,
    TIsoTimezone, TUser } from '@shared/models';
const DateTimeFormats = Constants.DateTimeFormats; 

@Component({
    'selector': 'app-accountNew',
    'templateUrl': './accountNew.page.html',
    'styleUrls': ['./accountNew.page.scss'],
    'standalone': true,
    'imports': [ CommonModule, FormsModule, IonicModule ]
})
export class AccountNewPage extends ViewBasePage implements OnInit, OnDestroy {
    private isoCountries: TIsoCountry[];
    // public isoTimezones: TIsoTimezones;
    public timezones: TIsoTimezone[];
    public regions: TRegion[];
    public vm: any = { 'role': 'manager', 'timezone': {} };
    private defaults: any = { 'efficiency': true, 'customer': true, 'campaign': true };
    public submitted = false;

    // private accountCreatorService: AccountCreatorService,
    constructor(private authService: AuthService, private isoCountriesService: IsoCountriesService,
        private usersService: UsersService, private addressVerificationService: AddressVerificationService,
        private isoTimezonesService: IsoTimezonesService, injector: Injector) {
        super(injector);
    }

    public override async ngOnInit() {
        super.ngOnInit();
        this.vm.timezone = this.isoTimezonesService.getNullable();

        const countries = await this.isoCountriesService.getAll();
        this.isoCountries = this.isoCountriesService.sortCountries(countries);

        this.vm.efficiency = true;
        this.vm.customer = true;
        this.vm.campaign = true;

        this.vm.efficiencyDesc = 'Feature Enabled';
        this.vm.customerDesc = 'Feature Enabled';
        this.vm.campaignDesc = 'Feature Enabled';

        this.timezones = await this.isoTimezonesService.get();
        this.vm.countryName = 'United States of America';
        this.loadCountry(this.vm.countryName);

        // this.vm.countryAbbr = 'USA';
        // this.vm.countryCode = '+1';
        this.vm.addressVerified = false;
    }

    public override ngOnDestroy() {
        super.ngOnDestroy();
    }

    public async onZipcodeChange() {
        if (this.vm.zipcode.length === 5) {
            try {
                await this.uxHelperService.showSpinner('Loading data.');
                // const serviceArea: TServiceArea = await this.serviceAreasService.getByUid(zipcode);
                await this.uxHelperService.hideSpinner();

                // if (serviceArea) {
                //     this.useZipcodeCity(serviceArea.city, serviceArea.zipcode);
                // } else {
                //     await this.uxHelperService.showAlert('Error',
                //                          `${serviceArea.city}, ${serviceArea.zipcode}: is not one of our service areas.`);
                // }
            } catch (err) {
                await this.uxHelperService.hideSpinner();
                await this.uxHelperService.showAlert('Error', JSON.stringify(err));
            }
        }
    }

    public async onTimezoneClick() {
        const items = this.timezones.map(o => o.caption);

        const options: ModalOptions = {
            component: RadioModalPage,
            componentProps: { 'items': items, 'selectedItem': this.vm.regionName, 'title': 'Select State / Region' },
            backdropDismiss: false,
            showBackdrop: true,
            cssClass: 'myModalSize'
        };

        const myModal = await this.modalController.create(options);
        await myModal.present();
        const response: any = await myModal.onDidDismiss();
        if (response.data.okay) {
            const timezone = this.timezones.find(o => (o.caption === response.data.selectedItem[0]));

            // this.vm.timezoneName = timezone.timezoneName;
            this.vm.timezone = timezone;
        }
    }

    public async onRegionNameClick() {
        const items = this.regions.map(o => o.regionName);

        const options: ModalOptions = {
            component: RadioModalPage,
            componentProps: { 'items': items, 'selectedItem': this.vm.regionName, 'title': 'Select State / Region' },
            backdropDismiss: false,
            showBackdrop: true,
            cssClass: 'myModalSize'
        };

        const myModal = await this.modalController.create(options);
        await myModal.present();
        const response: any = await myModal.onDidDismiss();
        if (response.data.okay) {
            const region = this.regions.find(o => o.regionName === response.data.selectedItem);

            this.vm.regionName = region.regionName;
            this.vm.regionCode = region.regionCode;
        }
    }

    public async onCountryClick() {
        const items = this.isoCountries.map(o => o.countryName);

        const options: ModalOptions = {
            component: RadioModalPage,
            componentProps: {
                'items': items,
                'selectedItem': this.vm.countryName,
                'title': 'Select Country'
            },
            backdropDismiss: false,
            showBackdrop: true,
            cssClass: 'myModalSize'
        };

        const myModal = await this.modalController.create(options);
        await myModal.present();
        const response: any = await myModal.onDidDismiss();
        if (response.data.okay) {
            this.loadCountry(response.data.selectedItem);
            // const country = this.isoCountries.find(o => {
            //     return (o.countryName === response.data.selectedItem);
            // });
            // this.vm.countryName = country.countryName;
            // this.vm.countryAbbr = country.countryAbbr;
            // this.vm.countryCode = country.isoCountryCode;
            // this.regions        = country.regions;
        }
    }

    private loadCountry(countryName: string) {
        const country = this.isoCountries.find(o => o.countryName === countryName);
        this.vm.countryName = country.countryName;
        this.vm.countryAbbr = country.countryAbbr;
        this.vm.countryCode = country.isoCountryCode;
        this.vm.phonePrefixes = country.phonePrefixes;
        this.regions = country.regions;
    }

    public computeFullAddress(): TAddress {
        // The user may have entered invalid chars.  Remove them.  
        this.vm.streetAddress = this.utilsService.replaceCh(this.vm.streetAddress, '.#', '');

        this.vm.fullAddress = this.utilsService.formatAddressForGoogleMaps(
            this.vm.streetAddress, this.vm.city, this.vm.regionName,
            this.vm.zipcode, this.vm.countryAbbr);

        const address: TAddress = {
            city: this.vm.city,
            coordinate: null,
            countryName: this.vm.countryName,
            isoCountryCode: this.vm.countryCode,
            // isoRegionCode: this.vm.regionCode,
            fullAddress: this.vm.fullAddress,
            regionName: this.vm.regionName,
            regionCode: this.vm.regionCode,
            streetAddress: this.vm.streetAddress,
            zipcode: this.vm.zipcode,
            countryAbbr: ''
        };

        return address;
    }

    public isAddressValid(): boolean {
        return this.vm.addressVerified;
    }

    public async onValidateAddressClick() {
        try {
            await this.uxHelperService.showSpinner('Verifing address.');

            const testAddress = this.computeFullAddress();
            const validation = await this.addressVerificationService.verifyAddress(testAddress);

            // const response: any = await this.firebaseFunctionsService.convertStreetAddressToLatLng(fullAddress);
            // const validation: any = this.validateAddress(this.vm, response);

            if (validation) {
                this.vm.coordinate = validation.location.coordinate;
                this.vm.addressVerified = true;
                this.vm.addressVerifiedMsg = validation.statusMessage;
                this.vm.formattedAddress = validation.location.formattedAddress;
            }
            await this.uxHelperService.showAlert(validation.statusTitle, validation.location.formattedAddress);
        } catch (err) {
            this.vm.coordinate = null;
            this.vm.addressVerified = false;
            this.vm.addressVerifiedMsg = 'Address Not Found.';
            await this.uxHelperService.hideSpinner();
            const msg = 'Do NOT specify unit or apartment numbers and only use periods if needed.';
            await this.uxHelperService.showAlert('Address Not Found', msg);
        } finally {
            await this.uxHelperService.hideSpinner();
        }
    }

    public async onSaveClick(form: NgForm) {
        this.submitted = true;
        if (!form.valid) {
            return;
        }

        const isEmailAvailable = await this.authService.isEmailAddressAvailable(this.vm.email);
        if (!isEmailAvailable) {
            await this.uxHelperService.showAlert('Invalid Email', 'This email address is already used.');
            return;
        }

        const user = await this.usersService.getByCellPhone(this.vm.cellPhone);
        if (user) {
            await this.uxHelperService.showAlert('Invalid Cell Number', 'This cell phone number is already used.');
            return;
        }

        try {
            await this.uxHelperService.showSpinner('Creating account.');
            // const response: any = await this.accountCreatorService.registerAccount(this.vm);
            const info = this.buildCreateAccountInfo();
            // console.log('info', info);
            const response: any = await this.firebaseFunctionsService.createAccount(info);
            await this.uxHelperService.hideSpinner();

            // Do not automatically go back to accounts.  We need to display the password.
            // this.router.navigateByUrl('/accounts');

            if (response) {
                this.vm.password = response.password;
                this.vm.claims = JSON.stringify(response.claims);

                let msg = 'Success. Your account has been created with login credentials: ';
                msg += 'Email = ' + this.vm.email + ', Password = ' + response.password;
                msg += '.  The first thing you should do is go to the login page and change your password.';
                await this.uxHelperService.showAlert('Important', msg);
            } else {
                await this.uxHelperService.showAlert('Error', 'Something went wrong.');
            }
        } catch (err) {
            this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        }
    }

    private buildCreateAccountInfo(): TCreateAccountInfo {
        const expires = this.dateService.addToNow(15, 'days', true);
        // const expirationDate = this.dateService.formatTimestamp(expires, DateTimeFormats.ShortDate)

        const user: TUser = {
            'firstName': this.vm.firstName,
            'lastName': this.vm.lastName,
            'cellPhone': this.utilsService.unformatPhoneNumber(this.vm.cellPhone),
            'email': this.vm.email,
            'role': this.vm.role,
            'roleTitle': this.utilsService.getRoleTitle(this.vm.role),
            'uid': null
        };

        const questionsAndAnswers: TAccountCreationQuestionsAndAnswers = {
            'hasReliableInternet': true,
            'acceptsLearningCurve': true,
            'isServiceManager': true,
            'acceptsTrialTerms': true,
            'commitsToOnboarding': true
        };

        const info: TCreateAccountInfo = {
            'questionsAndAnswers': questionsAndAnswers,
            
            // Manager Info
            'user': user,
            // 'firstName': this.vm.firstName,
            // 'lastName': this.vm.lastName,
            // 'cellPhone': this.utilsService.unformatPhoneNumber(this.vm.cellPhone),
            // 'email': this.vm.email,
            // 'roleTitle': this.utilsService.getRoleTitle(this.vm.role),
            // 'userUid': null,

            // Dealership Info
            // 'dealershipName': this.vm.dealershipName,
            // 'phoneNumber': this.utilsService.unformatPhoneNumber(this.vm.phoneNumber),
            // 'timezoneName': this.vm.timezone.timezoneName,
            // 'timezone': this.vm.timezone.timezoneName,
            // 'countryAbbr': this.vm.countryAbbr,
            // 'countryName': this.vm.countryName,
            // 'countryCode': this.vm.countryCode,
            // 'isoCountryCode': this.vm.countryCode,
            // 'isoRegionCode': this.vm.regionCode,
            // 'isoRegionName': this.vm.regionName,
            // 'fullAddress': this.vm.fullAddress,
            // 'streetAddress': this.vm.streetAddress,
            // 'zipcode': this.vm.zipcode,
            // 'city': this.vm.city,
            // 'coordinate': {
            //     latitude: this.vm.coordinate.latitude,
            //     longitude: this.vm.coordinate.longitude,
            // },

            // Other Info
            // 'phonePrefixes': this.vm.phonePrefixes,
            'packageName': 'Trial Package',
            // 'expirationDate': expirationDate,
            'expirationDate': expires,
            'tags': [],
            'lists': [],
            // this.vm.countryName   = country.countryName;
            // this.vm.countryAbbr   = country.countryAbbr;
            // this.vm.countryCode   = country.isoCountryCode;
            'accountNumber': this.vm.accountNumber,
            'affiliateId': this.vm.affiliateId,
            'signupCouponCode': this.vm.couponCode,
            // dealership: undefined,
            // isoRegionCode: ''
        };
        return info;
    }

    // private validateAddress(requestedAddress: any, response: any): any {
    //     this.vm.addressVerifiedMsg = '';
    //     this.vm.formattedAddress = '';

    //     // These values are returned from the firebase function.
    //     // const status = response.status;
    //     const result = response.results[0];
    //     const locationType = result.geometry.location_type;
    //     const addressComponents = result.address_components;

    //     // The user only typed in a street number, a street name and a zipcode.
    //     // Even though the firebase function returned a lot more information like city, county, country etc.
    //     // we only need to validate the three typed in values.
    //     const foundStreetNums  = this.extractValuesFromAddressComponentsByType(addressComponents, 'street_number');
    //     const foundStreetNames = this.extractValuesFromAddressComponentsByType(addressComponents, 'route');
    //     const foundZipcodes    = this.extractValuesFromAddressComponentsByType(addressComponents, 'postal_code');

    //     // The user typed in these requested values.
    //     const requestedZipcode = requestedAddress.zipcode.toString();
    //     const parts = requestedAddress.streetAddress.split(' ');
    //     const requestedStreetNumber = parts[0].toString();
    //     // Remove the street number to be left with the street name
    //     const addr = requestedAddress.streetAddress.toString();
    //     const requestedStreetName = addr.replace(requestedStreetNumber, '').trim().toString();

    //     // Verify that the typed in values resulted in a good address.
    //     const streetNumberVerified = this.verifyValue(requestedStreetNumber, foundStreetNums);
    //     const streetNameVerified   = this.verifyValue(requestedStreetName, foundStreetNames);
    //     const zipcodeVerified      = this.verifyValue(requestedZipcode, foundZipcodes);

    //     //
    //     // const zipcode1 = this.isZipcodeServiceable(foundZipcodes.long_name);
    //     // const zipcode2 = this.isZipcodeServiceable(requestedZipcode);
    //     // const isZipcodeServiceable = zipcode1 && zipcode2;

    //     //
    //     let statusMsg: string;
    //     if (!zipcodeVerified) {
    //         statusMsg = 'Address not found in zipcode ' + requestedZipcode + 
    //              '.  Address was found in zipcode ' + foundZipcodes.long_name;
    //     } else if (!streetNameVerified) {
    //         statusMsg = 'Address not found on this street or street not found in this zipcode.';
    //     } else if (!streetNumberVerified) {
    //         statusMsg = 'Street number not found on this street.';
    //     } else {
    //         statusMsg = 'Exact address found.';
    //     }

    //     const validation: any = {
    //         'requestedAddress': requestedAddress,
    //         'location': {
    //             'formattedAddress': result.formatted_address,
    //             'locationType': locationType,
    //             'coordinate': {
    //                 'latitude': result.geometry.location.lat,
    //                 'longitude': result.geometry.location.lng
    //             }
    //         },
    //         'statusMsg': statusMsg
    //     };
    //     return validation;
    // }

    // private verifyValue(testValue: string, acceptableValues: any) {
    //     // Assume the user types in the address for Costco:  3411 St Rose Parkway
    //     // The firebase function returns short name:  3411 St Rose Pkwy and long name: 3411 Saint Rose Parkway
    //     // The user doesn't realize he typed in a combination of short and long names.
    //     // So, we need to do a search over both short and long names.

    //     if (!testValue || !acceptableValues || !acceptableValues.long_name || !acceptableValues.short_name) {
    //         return false;
    //     }

    //     const testwords = testValue.toLowerCase().split(' ');

    //     const acceptablevalue1 = acceptableValues.long_name.toLowerCase();
    //     const acceptablevalue2 = acceptableValues.short_name.toLowerCase();

    //     const allAcceptableValues = acceptablevalue1 + ' ' + acceptablevalue2;
    //     const words: string[] = allAcceptableValues.split(' ');

    //     let allTestWordsAcceptable = true;
    //     testwords.forEach(word => {
    //         const found = words.includes(word);
    //         if (!found) {
    //             allTestWordsAcceptable = false;
    //         }
    //     });

    //     return allTestWordsAcceptable;

    //     // Do not do this:
    //     // Search for encia in the string = 'valencia boulevard valencia blvd'
    //     // The above string search will return true.  Obviously there is no street named encia.
    // }

    // private extractValuesFromAddressComponentsByType(components: any, type: string) {
    //     let comp: any = null;
    //     let idx = -1;
    //     components.forEach(component => {
    //         ++idx;
    //         const types = component.types;
    //         types.forEach(typ => {
    //             if (typ === type) {
    //                 comp = component;
    //             }
    //         });
    //     });
    //     return comp;
    //     // return components[idx];
    // }

    // private computeFeatureDesc(): string {
    //     let desc: string;
    //     const dFeature = this.vm.efficiency || false;
    //     const mFeature = this.vm.customer || false;
    //     const cFeature = this.vm.campaign || false;

    //     if (dFeature) {
    //         desc = 'Efficiency Features.';
    //     }
    //     if (mFeature) {
    //         desc += '  Customer Features.';
    //     }
    //     if (cFeature) {
    //         desc += '  Campaign Features.';
    //     }
    //     return desc;
    // }

    // public onToggleEfficiencyClick() {
    //     this.vm.efficiencyDesc = 'Feature ';
    //     this.vm.efficiencyDesc += (this.vm.efficiency) ?
    //          'Enabled' : 'Disabled';
    // }

    // public onToggleCustomerClick() {
    //     this.vm.customerDesc = 'Feature ';
    //     this.vm.customerDesc += (this.vm.customer) ? 'Enabled' : 'Disabled';
    // }

    // public onToggleCampaignClick() {
    //     this.vm.campaignDesc = 'Feature ';
    //     this.vm.campaignDesc += (this.vm.campaign) ? 'Enabled' : 'Disabled';
    // }
    //
}
